import { hasConsent } from '@/helpers/cookies'
import i18n from '@/helpers/i18n'
import { Consts } from '@/helpers/consts'

const AUTH_METHOD_STANDARD = 'standard_auth'
const FIXED_PROVINCE = 'QC'
const FIXED_COUNTRY = 'CA'
const FIXED_CURRENCY = 'CAD'

const PROFESSIONAL_DOMAIN_LABEL_KEYS = {
  [Consts.PROFESSIONAL_DOMAINE_MANAGEMENT]: 'professional-domain-management',
  [Consts.PROFESSIONAL_DOMAINE_BUSINESS]: 'professional-domain-business',
  [Consts.PROFESSIONAL_DOMAINE_SCIENCES_TECHNOLOGY]: 'professional-domain-sciences-technology',
  [Consts.PROFESSIONAL_DOMAINE_HEALTH]: 'professional-domain-health',
  [Consts.PROFESSIONAL_DOMAINE_SOCIAL_SERVICES]: 'professional-domain-sciences-social-services',
  [Consts.PROFESSIONAL_DOMAINE_CULTURE]: 'professional-domain-culture',
  [Consts.PROFESSIONAL_DOMAINE_SALES]: 'professional-domain-sales',
  [Consts.PROFESSIONAL_DOMAINE_CONSTRUCTION]: 'professional-domain-construction',
  [Consts.PROFESSIONAL_DOMAINE_NATURE]: 'professional-domain-nature',
  [Consts.PROFESSIONAL_DOMAINE_MANUFACTURING]: 'professional-domain-manufacturing'
}

const STUDY_DOMAIN_LABEL_KEYS = {
  [Consts.FIELD_OF_STUDY_ADMIN]: 'field-of-study-admin',
  [Consts.FIELD_OF_STUDY_TOURISM]: 'field-of-study-tourism',
  [Consts.FIELD_OF_STUDY_ARTS]: 'field-of-study-arts',
  [Consts.FIELD_OF_STUDY_COMMUNICATION]: 'field-of-study-communication',
  [Consts.FIELD_OF_STUDY_EDUCATION]: 'field-of-study-education',
  [Consts.FIELD_OF_STUDY_ENVIRONMENT]: 'field-of-study-environment',
  [Consts.FIELD_OF_STUDY_MECHANICS]: 'field-of-study-mechanics',
  [Consts.FIELD_OF_STUDY_MINES]: 'field-of-study-mines',
  [Consts.FIELD_OF_STUDY_HEALTH]: 'field-of-study-health',
  [Consts.FIELD_OF_STUDY_HUMANITIES]: 'field-of-study-humanities',
  [Consts.FIELD_OF_STUDY_SCIENCES]: 'field-of-study-sciences',
  [Consts.FIELD_OF_STUDY_IT]: 'field-of-study-it',
  [Consts.FIELD_OF_STUDY_TRANSPORT]: 'field-of-study-transport'
}

const DIPLOMA_VALUES = {
  [Consts.DIPLOMA_DEP]: 'dep',
  [Consts.DIPLOMA_AEC]: 'aec',
  [Consts.DIPLOMA_DEC]: 'dec',
  [Consts.DIPLOMA_BAC]: 'bachelor',
  [Consts.DIPLOMA_MASTER]: 'master',
  [Consts.DIPLOMA_DOCTORAT]: 'doctorate',
  [Consts.DIPLOMA_DESS]: 'dess'
}

const INSTITUTION_LEVEL_VALUES = {
  [Consts.INSTITUTION_LEVEL_PROFESSIONAL_VALUE]: 'vocational',
  [Consts.INSTITUTION_LEVEL_COLLEGE_VALUE]: 'college',
  [Consts.INSTITUTION_LEVEL_UNIVERSITY_VALUE]: 'university'
}

function pushToDataLayer(payload) {
  if (!hasConsent('tracking')) {
    return
  }

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(payload)
}

function toIsoCountryCode(code) {
  if (!code) {
    return undefined
  }
  return String(code).toUpperCase()
}

function firstNationality(nationalities) {
  if (!nationalities || nationalities.length === 0) {
    return undefined
  }
  return toIsoCountryCode(nationalities[0].countryCode)
}

function frLabel(keyMap, enumValue) {
  if (!enumValue) {
    return undefined
  }
  const key = keyMap[String(enumValue)]
  if (!key) {
    return undefined
  }
  return i18n.instance().t(key, 'fr')
}

function firstDomainLabel(keyMap, domains) {
  if (!domains || domains.length === 0) {
    return undefined
  }
  return frLabel(keyMap, domains[0])
}

function mapSalaryPeriod(salaryBracketType) {
  if (salaryBracketType === Consts.SALARY_RANGE_HOURLY) {
    return 'hourly'
  }
  if (salaryBracketType === Consts.SALARY_RANGE_YEARLY) {
    return 'annual'
  }
  return undefined
}

function findLocationCity(locations, locationId) {
  if (!locationId || locationId === false || !locations) {
    return undefined
  }
  const match = locations.find((location) => location.id === locationId)
  return match ? match.location || undefined : undefined
}

function firstMissionCountry(countries) {
  if (!countries || countries.length === 0) {
    return undefined
  }
  return toIsoCountryCode(countries[0].countryCode)
}

function pickProgramDuration(version) {
  if (!version) {
    return undefined
  }
  if (version.durationInHours !== null && version.durationInHours !== undefined) {
    return { value: version.durationInHours, unit: 'hours' }
  }
  if (version.durationInMonths !== null && version.durationInMonths !== undefined) {
    return { value: version.durationInMonths, unit: 'months' }
  }
  if (version.durationInYears !== null && version.durationInYears !== undefined) {
    return { value: version.durationInYears, unit: 'years' }
  }
  return undefined
}

function trackLogin(userId) {
  pushToDataLayer({
    event: 'login',
    user_id: userId || undefined,
    method: AUTH_METHOD_STANDARD,
    login_status: 'logged_in'
  })
}

function trackSignUp(userId) {
  pushToDataLayer({
    event: 'sign_up',
    user_id: userId || undefined,
    method: AUTH_METHOD_STANDARD,
    login_status: 'logged_out'
  })
}

function trackJobApplicationSubmission({
  userId,
  nationalities,
  locations,
  locationId,
  missionCountries,
  employerName,
  jobVersion
}) {
  const version = jobVersion || {}
  pushToDataLayer({
    event: 'job_application_submission',
    user_id: userId || undefined,
    details: {
      applicant: {
        nationality: firstNationality(nationalities)
      },
      recruitment: {
        city: findLocationCity(locations, locationId),
        country: firstMissionCountry(missionCountries)
      },
      employer: {
        name: employerName,
        province: FIXED_PROVINCE,
        country: FIXED_COUNTRY
      },
      job: {
        title: version.translationOrDefault ? version.translationOrDefault.title : undefined,
        domain: firstDomainLabel(PROFESSIONAL_DOMAIN_LABEL_KEYS, version.domains),
        cnp_code: version.nocCode != null ? String(version.nocCode) : undefined,
        positions_available: version.numberOfPositions != null ? version.numberOfPositions : undefined,
        hours_per_week: version.hoursPerWeek != null ? version.hoursPerWeek : undefined,
        salary: {
          period: mapSalaryPeriod(version.salaryBracketType),
          minimum: version.salaryBracketLow != null ? version.salaryBracketLow : undefined,
          maximum: version.salaryBracketHigh != null ? version.salaryBracketHigh : undefined,
          currency: FIXED_CURRENCY
        }
      }
    }
  })
}

function trackStudyApplicationSubmission({
  userId,
  nationalities,
  locations,
  locationId,
  missionCountries,
  institution,
  programVersion
}) {
  const version = programVersion || {}
  const institutionLevel = institution && institution.institutionLevel
  pushToDataLayer({
    event: 'study_application_submission',
    user_id: userId || undefined,
    details: {
      applicant: {
        nationality: firstNationality(nationalities)
      },
      recruitment: {
        city: findLocationCity(locations, locationId),
        country: firstMissionCountry(missionCountries)
      },
      institution: {
        name: institution ? institution.name : undefined,
        location: institution && institution.city ? institution.city : undefined,
        province: FIXED_PROVINCE,
        country: FIXED_COUNTRY
      },
      program: {
        title: version.translationOrDefault ? version.translationOrDefault.title : undefined,
        domain: firstDomainLabel(STUDY_DOMAIN_LABEL_KEYS, version.domains),
        level: institutionLevel ? INSTITUTION_LEVEL_VALUES[String(institutionLevel)] : undefined,
        diploma: version.diploma != null ? DIPLOMA_VALUES[String(version.diploma)] : undefined,
        duration: pickProgramDuration(version)
      }
    }
  })
}

export default {
  pushToDataLayer,
  trackLogin,
  trackSignUp,
  trackJobApplicationSubmission,
  trackStudyApplicationSubmission
}
